import { useState } from 'react';
import { UserPlus, Mail, Users, Loader2, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useContactMatching } from '@/hooks/useContactMatching';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface FindFriendsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FindFriendsDialog({ isOpen, onClose }: FindFriendsDialogProps) {
  const navigate = useNavigate();
  const [emailInput, setEmailInput] = useState('');
  const [nonMatchedEmails, setNonMatchedEmails] = useState<string[]>([]);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const { matchContacts, sendInvites, matches, isMatching, isSendingInvites, setMatches } = useContactMatching();

  const parseEmails = (input: string): string[] => {
    // Split by comma, newline, semicolon, or space
    const emails = input
      .split(/[,;\n\s]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return email && emailRegex.test(email);
      });

    return [...new Set(emails)]; // Remove duplicates
  };

  const handleFindFriends = async () => {
    const emails = parseEmails(emailInput);

    if (emails.length === 0) {
      toast({
        title: 'No valid emails',
        description: 'Please enter at least one valid email address.',
        variant: 'destructive',
      });
      return;
    }

    const matched = await matchContacts(emails);
    
    // Calculate non-matched emails
    const matchedEmails = new Set(matched.map((m: any) => m.email_hash));
    const nonMatched = emails.filter(email => {
      // We can't check directly, so we show all as potentially non-matched for invite
      return true;
    });
    
    // For simplicity, if we got matches, assume the rest are non-matched
    if (matched.length < emails.length) {
      setNonMatchedEmails(emails.slice(matched.length));
    } else {
      setNonMatchedEmails([]);
    }
  };

  const handleFollow = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('follows')
        .insert({
          follower_id: (await supabase.auth.getUser()).data.user?.id,
          following_id: userId,
          status: 'accepted', // Auto-accept for simplicity
        });

      if (error) throw error;

      setFollowingIds(prev => new Set(prev).add(userId));
      
      toast({
        title: 'Followed!',
        description: 'You are now following this user.',
      });
    } catch (error) {
      console.error('Error following user:', error);
      toast({
        title: 'Failed to follow',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSendInvites = async () => {
    if (nonMatchedEmails.length === 0) {
      const allEmails = parseEmails(emailInput);
      await sendInvites(allEmails);
    } else {
      await sendInvites(nonMatchedEmails);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    setEmailInput(text);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Find Friends on Dino
          </DialogTitle>
          <DialogDescription>
            Enter email addresses to find which of your contacts are already on Dino, or invite them to join.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="emails">Email Addresses</Label>
            <Textarea
              id="emails"
              placeholder="Enter email addresses separated by commas, spaces, or new lines&#10;&#10;Example:&#10;friend1@example.com&#10;friend2@example.com, friend3@example.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              rows={6}
              className="font-mono text-sm"
            />
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('csv-upload')?.click()}
                disabled={isMatching}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV/TXT
              </Button>
              <input
                id="csv-upload"
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              <p className="text-xs text-muted-foreground">
                {parseEmails(emailInput).length} valid email(s) detected
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleFindFriends}
              disabled={isMatching || emailInput.trim().length === 0}
              className="flex-1"
            >
              {isMatching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Find Friends
                </>
              )}
            </Button>
          </div>

          {/* Matches */}
          {matches.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">
                Found {matches.length} friend{matches.length === 1 ? '' : 's'} on Dino
              </h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {matches.map((match) => (
                  <div
                    key={match.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar 
                        className="cursor-pointer"
                        onClick={() => {
                          navigate(`/u/${match.username}`);
                          onClose();
                        }}
                      >
                        <AvatarImage src={match.image_thumb || undefined} />
                        <AvatarFallback>{match.name?.[0] || match.username?.[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p 
                          className="font-medium cursor-pointer hover:underline"
                          onClick={() => {
                            navigate(`/u/${match.username}`);
                            onClose();
                          }}
                        >
                          {match.name || match.username}
                        </p>
                        <p className="text-xs text-muted-foreground">@{match.username}</p>
                      </div>
                    </div>
                    {match.isFollowing || followingIds.has(match.id) ? (
                      <Button variant="outline" size="sm" disabled>
                        Following
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleFollow(match.id)}
                      >
                        Follow
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invite Non-Matched */}
          {(matches.length > 0 || nonMatchedEmails.length > 0) && (
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleSendInvites}
                disabled={isSendingInvites}
                className="w-full"
              >
                {isSendingInvites ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending Invites...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Invite Friends Not on Dino
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                We'll send email invitations to contacts who aren't on Dino yet
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
